import React, {useState} from "react"
import {RecognitionAutomation, RecognitionAutomationRuleType, RecognitionAutomationStatus, RecognitionAutomationType} from "@irati/types"
import Flex from "@irati/ui/src/Flex"
import {Card, Hr} from "@irati/ui/src/Layout"
import {useUpdateRecognitionAutomationStatusMutation} from "@irati/recognition-automation/hooks"
import moment from "moment"
import Expander from "@irati/ui/src/Expander"
import {Body, H4, H5} from "@irati/ui/src/Typography"
import Pill from "@irati/ui/src/Pill"
import TeamName from "@irati-web/platform/src/components/TeamName"
import mainTheme from "@irati/ui/src/global/theme"
import FormattedMessage, {injectIntl} from "@irati/utils/lang"
import {InjectIntlProps} from "@irati/utils/lang/injectIntl"
import Button from "@irati/ui/src/Button"
import Spacing from "@irati/ui/src/Spacing"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import {AUTOMATION_BASE, AUTOMATION_EDIT} from "../../routes/routeConfig"
import AutomationConfirmationModal, {Operation} from "../AutomationConfirmationModal"
import AutomationDeactivateModal from "../AutomationDeactivateModal"
interface AutomationCountryItemProps extends InjectIntlProps {
  recognitionAutomation: RecognitionAutomation
}
const StatusPillConfiguration: Record<RecognitionAutomationStatus, {background: string; text: string}> = {
  [RecognitionAutomationStatus.Active]: {
    background: mainTheme.colors.success,
    text: "admin.automation.lbl.active"
  },
  [RecognitionAutomationStatus.Draft]: {
    background: mainTheme.colors.greys.grey50,
    text: "admin.automation.lbl.inactive"
  },
  [RecognitionAutomationStatus.Deactivated]: {
    background: mainTheme.colors.utility.warning,
    text: "admin.automation.lbl.deactived"
  }
}
const AutomationCountryItem = (props: AutomationCountryItemProps) => {
  const {recognitionAutomation, formatMessage} = props
  const [updateStatus, {data: recognitionAutomationUpdated}] = useUpdateRecognitionAutomationStatusMutation()
  const statusConfiguration = StatusPillConfiguration[recognitionAutomation.status!]
  const [submitPromise, setSubmitPromise] = useState()
  const [showConfirmDeactivate, setShowConfirmDeactivate] = useState(false)
  const updatedDate = recognitionAutomation.metadata?.updatedAt || recognitionAutomation.metadata?.createdAt
  const updatedByUser = recognitionAutomation.metadata?.updatedBy || recognitionAutomation.createdBy
  const RewardRulesLabel =
    recognitionAutomation.ruleType === RecognitionAutomationRuleType.Custom
      ? "admin.automation.lbl.customAmountRewardRules"
      : "admin.automation.lbl.yearlyAmountRewardRules"
  const primaryActionLbl =
    recognitionAutomation.status === RecognitionAutomationStatus.Active
      ? "admin.automation.btn.deactivateAutomation"
      : "admin.automation.btn.activateAutomation"
  const groupYearsPoints = recognitionAutomation.rewardRules?.reduce((acc, item) => {
    const prevYears = acc[item.amount!] || []
    return {
      ...acc,
      [item.amount!]: [...prevYears, formatMessage({id: "admin.automation.lbl.customYears"}, {years: item.year})]
    }
  }, {} as Record<string, string[]>)
  const switchStatus = () => {
    setShowConfirmDeactivate(false)
    const promise = updateStatus({
      variables: {
        status:
          recognitionAutomation.status === RecognitionAutomationStatus.Active
            ? RecognitionAutomationStatus.Deactivated
            : RecognitionAutomationStatus.Active,
        uuid: recognitionAutomation.uuid!
      }
    }).finally(() => {
      setSubmitPromise()
    })
    setSubmitPromise(promise)
  }
  const confirmChangeStatus = () => {
    if (recognitionAutomation.status === RecognitionAutomationStatus.Active) {
      setShowConfirmDeactivate(true)
      return
    }
    switchStatus()
  }
  const automationTypeIntlKey =
    recognitionAutomation.automationType === RecognitionAutomationType.RecognitionOnly
      ? "admin.automation.create.types.title.recognitionOnly"
      : "admin.automation.create.types.title.reward"
  return (
    <Card tiny bgColor={mainTheme.colors.greys.grey03}>
      <Expander
        fullWidth
        HeaderLabel={({children}) => children}
        showText={
          <Flex gap={3} marginLeft={1} alignItems="center">
            <H4>
              <TeamName team={recognitionAutomation.region} />
            </H4>
            <Pill text={formatMessage({id: statusConfiguration.text})} backgroundColor={statusConfiguration.background} isWhiteText />
          </Flex>
        }>
        <Spacing padding={1}>
          <Hr flush />
          <H5 marginBottom={1} marginTop={2}>
            <FormattedMessage id="admin.automation.lbl.lastUpdatedBy" />
          </H5>
          <Body>{`${updatedByUser?.firstName} ${updatedByUser?.lastName} - ${moment(updatedDate).format("DD MMMM YYYY")}`}</Body>
          <Hr />
          <H5 marginBottom={1}>
            <FormattedMessage id="admin.automation.lbl.automationType" />
          </H5>
          <Body>
            <FormattedMessage id={automationTypeIntlKey} />
          </Body>
          <Hr />
          <H5 marginBottom={1}>
            <FormattedMessage id="admin.automation.lbl.rewardRules" />
          </H5>
          {!recognitionAutomation.rewardRules?.length ||
          recognitionAutomation.automationType === RecognitionAutomationType.RecognitionOnly ? (
            <Body>-</Body>
          ) : (
            <>
              <FormattedMessage id={RewardRulesLabel} />
              {recognitionAutomation.ruleType === RecognitionAutomationRuleType.Yearly && (
                <Flex gap={1} marginTop={mainTheme.spacing.half} alignItems="center">
                  <Icon icon={IconTypes.rewardPoints} />
                  <Body small>{recognitionAutomation.rewardRules[0].amount}</Body>
                </Flex>
              )}
              {recognitionAutomation.ruleType === RecognitionAutomationRuleType.Custom &&
                groupYearsPoints &&
                Object.entries(groupYearsPoints).map(([amount, years]) => {
                  return (
                    <Flex gap={1} marginTop={mainTheme.spacing.half} alignItems="center" key={amount}>
                      <Icon icon={IconTypes.rewardPoints} />
                      <Body small>{`${amount} - ${years.join(", ")}`}</Body>
                    </Flex>
                  )
                })}
            </>
          )}
          <Hr />
          <H5 marginBottom={1}>
            <FormattedMessage id="admin.automation.lbl.rewardBudget" />
          </H5>
          <Body>
            {!recognitionAutomation.walletUser?.firstName ||
            recognitionAutomation.automationType === RecognitionAutomationType.RecognitionOnly ? (
              <Body>-</Body>
            ) : (
              `${recognitionAutomation.walletUser.firstName} ${recognitionAutomation.walletUser.lastName} - ${
                recognitionAutomation.budget?.categoryName || formatMessage({id: "admin.automation.lbl.unassignedBudget"})
              }`
            )}
          </Body>
          <Flex marginTop={2} gap={2}>
            {recognitionAutomation.status === RecognitionAutomationStatus.Draft && (
              <ButtonRouterLink to={`${AUTOMATION_BASE}${AUTOMATION_EDIT.replace(":uuid", recognitionAutomation.uuid!)}`} small primary>
                <FormattedMessage id="admin.automation.btn.continueSetup" />
              </ButtonRouterLink>
            )}
            {recognitionAutomation.status !== RecognitionAutomationStatus.Draft && (
              <>
                <Button small primary onClick={confirmChangeStatus} load={submitPromise}>
                  <FormattedMessage id={primaryActionLbl} />
                </Button>
                <ButtonRouterLink to={`${AUTOMATION_BASE}${AUTOMATION_EDIT.replace(":uuid", recognitionAutomation.uuid!)}`} small>
                  <FormattedMessage id="admin.automation.btn.edit" />
                </ButtonRouterLink>
              </>
            )}
          </Flex>
        </Spacing>
      </Expander>
      <AutomationDeactivateModal
        show={showConfirmDeactivate}
        onConfirmButton={switchStatus}
        onHide={() => setShowConfirmDeactivate(false)}
      />
      <AutomationConfirmationModal recognitionAutomation={recognitionAutomationUpdated} operation={Operation.TOGGLE_STATUS} />
    </Card>
  )
}

export default injectIntl(AutomationCountryItem)
